import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    RowData,
    TableOptions,
    useReactTable
} from '@tanstack/react-table';

import {
    TableRoot,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../ui/TableUI';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import SelectPageSize from '../SelectPageSize';
import { cn } from '@/utils/utils';
import PagingInfo from '../PagingInfo';
import Paginate from '../../pagination/Paginate';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/hooks/useStore';
import { SkeletonDataTable } from '../SkeletonTable';
import { useTranslation } from 'react-i18next';
import { ArrowDownZAIcon, ArrowUpDownIcon, ArrowUpZAIcon } from 'lucide-react';
import ScrollTableGuide from '@/components/general/ScrollTableGuide';

import './style.scss';

declare module '@tanstack/react-table' {
    // eslint-disable-next-line
    interface ColumnMeta<TData extends RowData, TValue> {
        headerClassName: string,
        cellClassName: string
    }
}

interface TableProps<TData, TValue> extends Partial<TableOptions<TData>> {
    columns: ColumnDef<TData, TValue>[],
    data: TData[],
    containerClassName?: string,
    headerClassName?: string,
    cellClassName?: string,
    noData?: React.ReactNode,
    isManualAll?: boolean,
    showTableFooter?: boolean,
    showPagination?: boolean,
    showPagingInfo?: boolean,
    showSelectPageSize?: boolean,
    loadingData?: boolean,
    selectPageSizes?: number[],
    fixedChildrenTableBody?: ReactNode,
    renderActions?: React.ReactNode,
    enableScrollToTopTable?: boolean,
    enableScrollResTable?: boolean,
    renderTotal?: React.ReactNode
}

export default observer(function TableCustomOnlyTwoDept<TData, TValue>({
    columns,
    data,
    containerClassName,
    headerClassName,
    cellClassName,
    noData,
    isManualAll = true,
    showTableFooter = true,
    showPagination = true,
    showPagingInfo = true,
    showSelectPageSize = true,
    loadingData = false,
    selectPageSizes,
    // select row
    enableRowSelection = false,
    fixedChildrenTableBody,
    renderActions,
    enableScrollToTopTable = true,
    enableScrollResTable = false,
    renderTotal,
    ...props
}: TableProps<TData, TValue>) {
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    // variables
    const columnSelect: ColumnDef<TData> = {
        accessorKey: 'select-box',
        header: ({ table }) => (
            <IndeterminateCheckbox
                {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler()
                }}
            />
        ),
        cell: ({ row }) => (
            <div>
                <IndeterminateCheckbox
                    {...{
                        checked: row.getIsSelected(),
                        disabled: !row.getCanSelect(),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: row.getToggleSelectedHandler()
                    }}
                />
            </div>
        ),
        meta: { cellClassName: 'sticky left-0', headerClassName: 'sticky left-0' },
        enableSorting: false,
        minSize: 10,
        size: 10,
        maxSize: 10
    };

    // hooks
    const { t } = useTranslation();

    // store
    const { apiStore: { isLoadingTable } } = useStore();

    // lifecycle
    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            const checkScrollable = () => {
                const hasScroll = el.scrollWidth > el.clientWidth;
                setShowHint(hasScroll);
            };

            requestAnimationFrame(() => {
                checkScrollable();
            });

            const handleScroll = () => {
                if (el.scrollLeft > 0) {
                    if (onFirstScroll.current === true) onFirstScroll.current = false;
                    setShowHint(false);
                }
            };

            el.addEventListener('scroll', handleScroll);
            // window.addEventListener('resize', checkScrollable);

            return () => {
                el.removeEventListener('scroll', handleScroll);
                // window.removeEventListener('resize', checkScrollable);
            };
        }
    }, [data]);

    // convert columns
    const columnsConvert = columns.map((column) => {
        switch (column?.id) {
            // case 'id':
            //     return {
            //         ...column,
            //         meta: column.meta || { cellClassName: `${!!enableRowSelection ? 'sticky left-[45px]' : 'sticky left-0'}`, headerClassName: `${!!enableRowSelection ? 'sticky left-[45px]' : 'sticky left-0'}` }
            //     };
            // case 'actions':
            //     return {
            //         ...column,
            //         meta: column.meta || { cellClassName: 'sticky right-0', headerClassName: 'sticky right-0' }
            //     };
            default:
                return column;
        }
    });

    const columnLength: number = [...columns as any[]].reduce((prev, cur) => {
        if (cur?.columns && cur?.columns?.length > 0) {
            prev += cur?.columns?.length;
        } else {
            prev += 1;
        }

        return prev;
    }, 0);

    // init table
    const table = useReactTable({
        data,
        columns: !enableRowSelection ?
            columnsConvert :
            [
                columnSelect,
                ...columnsConvert
            ],
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection,
        ...(isManualAll ?
            {
                manualExpanding: true,
                manualFiltering: true,
                manualGrouping: true,
                manualPagination: true,
                manualSorting: true
            } :
            {}),
        ...props
    });

    return (
        <div id='table-wrapper' className={containerClassName} ref={tableRef}>
            {
                renderTotal ?
                    (
                        <div
                            className={cn(
                                'flex flex-wrap-reverse items-center justify-between gap-x-6 gap-y-4 mb-5'
                            )}
                        >
                            <div className='flex flex-wrap items-center gap-x-[min(64px,10vw)] gap-y-4'>
                                <div>{renderTotal}</div>
                                {
                                    showSelectPageSize && (
                                        <div>
                                            <SelectPageSize
                                                pageSize={table.getState().pagination.pageSize}
                                                setPagination={table.setPagination}
                                                selectPageSizes={selectPageSizes}
                                            />
                                        </div>
                                    )
                                }
                            </div>

                            {renderActions && <>{renderActions}</>}
                        </div>
                    ) :
                    (
                        <div
                            className={cn(
                                'flex flex-wrap-reverse justify-between items-center',
                                (showSelectPageSize || renderActions) && 'mb-5',
                                (showSelectPageSize && renderActions) && 'gap-4 xl:gap-6'
                            )}
                        >
                            {
                                showSelectPageSize && (
                                    <div>
                                        <SelectPageSize
                                            pageSize={table.getState().pagination.pageSize}
                                            setPagination={table.setPagination}
                                            selectPageSizes={selectPageSizes}
                                        />
                                    </div>
                                )
                            }
                            {renderActions && <>{renderActions}</>}
                        </div>
                    )
            }
            <div className='relative'>
                <ScrollTableGuide showHint={enableScrollResTable && showHint && onFirstScroll.current === true} />
                <TableRoot scrollWrapperRef={scrollRef}>
                    <TableHeader>
                        <TableRow>
                            {table.getHeaderGroups()[0].headers.map((header, idx) => {
                                return (
                                    <TableHead
                                        key={header.id || idx}
                                        className={cn(
                                            'whitespace-nowrap',
                                            header.column.getCanSort() && 'cursor-pointer select-none',
                                            headerClassName,
                                            header.column.columnDef.meta?.headerClassName
                                        )}
                                        style={{ width: header.getSize() }}
                                        onClick={header.column.getToggleSortingHandler()}
                                        colSpan={header.colSpan}
                                        rowSpan={header.isPlaceholder ? 2 : 1}
                                        title={
                                            header.column.getCanSort() ?
                                                header.column.getNextSortingOrder() === 'asc' ?
                                                    t('table.sort.ascending') :
                                                    header.column.getNextSortingOrder() === 'desc' ?
                                                        t('table.sort.descending') :
                                                        t('table.sort.clear') :
                                                undefined
                                        }
                                    >
                                        {
                                            header.isPlaceholder ?
                                                header.subHeaders?.length > 0 ?
                                                    header.subHeaders.map(sub => (
                                                        <div
                                                            className={cn(
                                                                'flex items-center justify-start gap-1'
                                                            )}
                                                            key={sub.id}
                                                        >
                                                            {
                                                                flexRender(
                                                                    sub.column.columnDef.header,
                                                                    sub.getContext()
                                                                )
                                                            }
                                                            {
                                                                sub.column.getCanSort() &&
                                                                (
                                                                    {
                                                                        asc: <ArrowUpZAIcon className='inline-block w-4 h-4' />,
                                                                        desc: <ArrowDownZAIcon className='inline-block w-4 h-4' />
                                                                    }[sub.column.getIsSorted() as string] ?? <ArrowUpDownIcon className='inline-block w-4 h-4' />
                                                                )
                                                            }
                                                        </div>
                                                    )) :
                                                    null :
                                                (
                                                    <div className={cn(
                                                        'flex items-center justify-start gap-1',
                                                        headerClassName,
                                                        header.column.columnDef.meta?.headerClassName
                                                    )}
                                                    >
                                                        {
                                                            flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )
                                                        }
                                                        {
                                                            header.column.getCanSort() &&
                                                            (
                                                                {
                                                                    asc: <ArrowUpZAIcon className='inline-block w-4 h-4' />,
                                                                    desc: <ArrowDownZAIcon className='inline-block w-4 h-4' />
                                                                }[header.column.getIsSorted() as string] ?? <ArrowUpDownIcon className='inline-block w-4 h-4' />
                                                            )
                                                        }
                                                    </div>
                                                )
                                        }
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                        <TableRow className='row-dept-two border-none'>
                            {table.getHeaderGroups()[0].headers.filter(headerItem => !headerItem.isPlaceholder).map((header) => {
                                return (
                                    header.subHeaders.map(sub => (
                                        <TableHead
                                            key={sub.id}
                                            className={cn(
                                                'whitespace-nowrap',
                                                sub.column.getCanSort() && 'cursor-pointer select-none',
                                                sub.column.columnDef.meta?.headerClassName
                                            )}
                                            style={{ width: sub.getSize() }}
                                            onClick={sub.column.getToggleSortingHandler()}
                                            colSpan={sub.colSpan}
                                            title={
                                                sub.column.getCanSort() ?
                                                    sub.column.getNextSortingOrder() === 'asc' ?
                                                        t('table.sort.ascending') :
                                                        sub.column.getNextSortingOrder() === 'desc' ?
                                                            t('table.sort.descending') :
                                                            t('table.sort.clear') :
                                                    undefined
                                            }
                                        >
                                            <div
                                                className={cn(
                                                    'flex items-center justify-start gap-1',
                                                    headerClassName,
                                                    sub.column.columnDef.meta?.headerClassName
                                                )}
                                            >
                                                {
                                                    flexRender(
                                                        sub.column.columnDef.header,
                                                        sub.getContext()
                                                    )
                                                }
                                                {
                                                    sub.column.getCanSort() &&
                                                    (
                                                        {
                                                            asc: <ArrowUpZAIcon className='inline-block w-4 h-4' />,
                                                            desc: <ArrowDownZAIcon className='inline-block w-4 h-4' />
                                                        }[sub.column.getIsSorted() as string] ?? <ArrowUpDownIcon className='inline-block w-4 h-4' />
                                                    )
                                                }
                                            </div>
                                        </TableHead>
                                    ))
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    {
                        (!loadingData || (loadingData && !isLoadingTable)) && (
                            <TableBody>
                                {table.getRowModel().rows?.length ?
                                    (
                                        <>
                                            {fixedChildrenTableBody}
                                            {/* <TableRow>
                                                <TableCell colSpan={7} className='text-center'>ABC</TableCell>
                                                <TableCell>ABC</TableCell>
                                                <TableCell>ABC</TableCell>
                                                <TableCell>ABC</TableCell>
                                                <TableCell>ABC</TableCell>
                                            </TableRow> */}
                                            {table.getRowModel().rows.map(row => (
                                                <TableRow
                                                    key={row.id}
                                                    data-state={row.getIsSelected() && 'selected'}
                                                >
                                                    {row.getVisibleCells().map(cell => (
                                                        <TableCell
                                                            key={cell.id}
                                                            className={cn('text-left', cellClassName, cell.column.columnDef.meta?.cellClassName)}
                                                        >
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </>
                                    ) :
                                    (
                                        <TableRow>
                                            <TableCell colSpan={enableRowSelection ? columnLength + 1 : columnLength} className='h-36 text-center'>
                                                {noData || t('sentences.no_data')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                            </TableBody>
                        )
                    }
                    {
                        loadingData && isLoadingTable && <SkeletonDataTable numCols={table.getAllColumns()?.length} />
                    }
                </TableRoot>
            </div>
            {
                (!loadingData || (loadingData && !isLoadingTable)) && showTableFooter && table.getRowCount() > 0 && (
                    <div className='mt-4 flex justify-between items-center flex-wrap'>
                        <div>
                            {
                                showPagingInfo && (
                                    <PagingInfo
                                        pageIndex={table.getState().pagination.pageIndex}
                                        pageSize={table.getState().pagination.pageSize}
                                        totalRecords={table.getRowCount()}
                                    />
                                )
                            }
                        </div>
                        <div>
                            {
                                showPagination && (
                                    <Paginate
                                        forcePage={table.getState().pagination.pageIndex}
                                        pageCount={table.getPageCount()}
                                        onPageChange={({ selected }) => {
                                            table.setPageIndex(selected);

                                            // scroll to table
                                            if (enableScrollToTopTable && tableRef.current) {
                                                const top = tableRef.current.offsetTop - 15;
                                                window.scrollTo({
                                                    top,
                                                    behavior: 'smooth'
                                                });
                                            }
                                        }}
                                        marginPagesDisplayed={2}
                                    />
                                )
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
});

function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: { indeterminate?: boolean } & React.HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    return (
        <input
            type='checkbox'
            ref={ref}
            className={className + ' cursor-pointer'}
            {...rest}
        />
    );
}
